import { supabase } from '../supabaseClient';

// ══════════════════════════════════════════════
// طبقة جلب بيانات المشروع — كانت كل الاستعلامات دي معمولة جوه fetchData()
// في App.jsx بشكل متتالي (await واحد ورا التاني)، يعني وقت التحميل الكلي
// = مجموع وقت الـ25 استعلام كلهم. ده كان أكبر سبب لبطء التحميل، خصوصًا على
// متصفح/جهاز جديد (اتصال بارد بسيرفر Supabase من غير أي اتصال سابق دافئ).
//
// هنا كل الاستعلامات بتتنفذ بالتوازي (Promise.allSettled)، فالوقت الكلي
// بقى = أطول استعلام واحد بس. استخدمنا allSettled مش Promise.all عشان لو
// جدول واحد فشل (شبكة/RLS)، الباقي يكمل عادي بدل ما الصفحة كلها تفضل فاضية.
// ══════════════════════════════════════════════

const buildQueries = (projectId) => ({
    clientComments: () => supabase
        .from('client_comments')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_read', false)
        .order('created_at', { ascending: false }),

    tasks: () => supabase
        .from('tasks')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),

    attendance: () => {
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        const start = startOfMonth.toISOString().split('T')[0];
        return supabase
            .from('attendance')
            .select('*')
            .eq('project_id', projectId)
            .gte('date', start)
            .order('date', { ascending: false });
    },

    dailyActivities: () => supabase
        .from('daily_activities')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),

    phases: () => supabase
        .from('project_phases')
        .select('*')
        .eq('project_id', projectId)
        .order('baseline_start', { ascending: true }),

    dependencies: () => supabase
        .from('phase_dependencies')
        .select('*')
        .eq('project_id', projectId),

    milestones: () => supabase
        .from('project_milestones')
        .select('*')
        .eq('project_id', projectId)
        .order('planned_date', { ascending: true }),

    // ✅ تصليح: كان بيفلتر بـ currentProjectId (متغيّر من الـ closure الخارجي)
    // بدل projectId (المُمرَّر فعليًا للدالة) — يعني عند تبديل مشروع، الـ
    // baseline revisions كانت بتتجاب من المشروع القديم مش الجديد.
    baselineRevisions: () => supabase
        .from('baseline_revisions')
        .select('*')
        .eq('project_id', projectId)
        .order('revision_number', { ascending: false }),

    leaves: () => supabase
        .from('leave_requests')
        .select('*, team_members(name, position, avatar)')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),

    challenges: () => supabase
        .from('task_challenges')
        .select('*')
        .eq('project_id', projectId),

    teamAssignments: () => supabase
        .from('project_team_assignments')
        .select(`
            *,
            team_members (
                id,
                name,
                name_arabic,
                position,
                employee_id,
                department,
                nationality,
                blood_type,
                phone,
                avatar,
                skills,
                status,
                employment_type,
                badge_issue_date,
                badge_expiry_date
            )
        `)
        .eq('project_id', projectId),

    budget: () => supabase
        .from('budget')
        .select('*')
        .eq('project_id', projectId),

    materials: () => supabase
        .from('materials')
        .select('*')
        .eq('project_id', projectId),

    performance: () => supabase
        .from('performance')
        .select('*')
        .eq('project_id', projectId)
        .order('date', { ascending: false }),

    photos: () => supabase
        .from('photos')
        .select('*')
        .eq('project_id', projectId)
        .order('uploaded_at', { ascending: false }),

    comments: () => supabase
        .from('comments')
        .select('*')
        .eq('project_id', projectId)
        .order('timestamp', { ascending: false }),

    replies: () => supabase
        .from('comment_replies')
        .select('*')
        .eq('project_id', projectId)
        .order('timestamp', { ascending: true }),

    invoices: () => supabase
        .from('invoices')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),

    clientAccount: () => supabase
        .from('client_account')
        .select('*')
        .eq('project_id', projectId)
        .order('transaction_date', { ascending: true })
        .order('created_at', { ascending: true }),

    documents: () => supabase
        .from('documents')
        .select('*')
        .eq('project_id', projectId)
        .order('uploaded_at', { ascending: false }),

    categories: () => supabase
        .from('budget_categories')
        .select('*')
        .eq('project_id', projectId)
        .order('created_date', { ascending: false }),

    expenses: () => supabase
        .from('budget_expenses')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),

    // ✅ Commitments — جدول جديد منفصل عن budget_expenses عن قصد: Commitment
    // (PO اتصدر/اتفق عليه بس لسه ما اتصرفش فعليًا) مختلف عن Expense (مصروف
    // فعلي اتسجل). فصلهم بيحافظ على AC في EVM زي ما هي (100% فعلي، زي ما
    // كانت قبل كده) وبيضيف رؤية إضافية في صفحة Budget Management بس.
    commitments: () => supabase
        .from('budget_commitments')
        .select('*')
        .eq('project_id', projectId)
        .order('commitment_date', { ascending: false }),

    rfis: () => supabase
        .from('rfi_tracker')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),

    contacts: () => supabase
        .from('communication_matrix')
        .select('*')
        .eq('project_id', projectId)
        .order('stakeholder_name'),

    hseIncidents: () => supabase
        .from('hse_incidents')
        .select('*')
        .eq('project_id', projectId)
        .order('incident_date', { ascending: false }),
});

/**
 * يجيب كل بيانات المشروع بالتوازي، ويرجّع { data, errors }.
 * - data: كل جدول اتحول لاسم الـ state المناسب في App.jsx، وبعد التحويلات/الحسابات
 *   اللي كانت بتتعمل بعد كل fetch (أرقام، بالانس تراكمي، تجميع replies/challenges...).
 * - errors: مصفوفة { table, message } لأي جدول فشل — استخدمها عشان تعرض توست حقيقي
 *   بدل ما يظهر "لا يوجد بيانات" بصمت.
 */
export async function fetchProjectData(projectId) {
    const queries = buildQueries(projectId);
    const keys = Object.keys(queries);

    const settled = await Promise.allSettled(keys.map((k) => queries[k]()));

    const raw = {};
    const errors = [];

    keys.forEach((key, i) => {
        const result = settled[i];
        if (result.status === 'fulfilled') {
            const { data, error } = result.value;
            if (error) {
                errors.push({ table: key, message: error.message });
                raw[key] = [];
            } else {
                raw[key] = data || [];
            }
        } else {
            errors.push({ table: key, message: result.reason?.message || String(result.reason) });
            raw[key] = [];
        }
    });

    // ── تحويلات/حسابات بعد الجلب (نفس منطق fetchData الأصلي في App.jsx) ──
    const budgetWithCalculations = raw.budget.map((d) => ({
        ...d,
        allocated: Number(d.allocated) || 0,
        spent: Number(d.spent) || 0,
        remaining: (Number(d.allocated) || 0) - (Number(d.spent) || 0),
    }));

    const performanceWithNumbers = raw.performance.map((d) => ({
        ...d,
        productivity: Number(d.productivity) || 0,
        quality: Number(d.quality) || 0,
        teamwork: Number(d.teamwork) || 0,
        reliability: Number(d.reliability) || 0,
    }));

    const invoicesWithNumbers = raw.invoices.map((d) => ({
        ...d,
        amount: Number(d.amount) || 0,
    }));

    let runningBalance = 0;
    const clientAccountWithCorrectBalance = raw.clientAccount.map((t) => {
        const debit = Number(t.debit) || 0;
        const credit = Number(t.credit) || 0;
        runningBalance = runningBalance + debit - credit;
        return { ...t, debit, credit, balance: runningBalance };
    });

    const categoriesWithNumbers = raw.categories.map((d) => ({
        ...d,
        allocated: Number(d.allocated) || 0,
    }));

    const expensesWithNumbers = raw.expenses.map((d) => ({
        ...d,
        amount: Number(d.amount) || 0,
    }));

    const commitmentsWithNumbers = raw.commitments.map((d) => ({
        ...d,
        amount: Number(d.amount) || 0,
    }));

    const teamMembers = raw.teamAssignments.map((assignment) => ({
        ...assignment.team_members,
        assigned_systems: assignment.assigned_systems,
        assigned_role: assignment.assigned_role,
        performance_score: assignment.performance_score,
    }));

    const taskChallenges = raw.challenges.reduce((acc, c) => {
        acc[c.task_id] = [...(acc[c.task_id] || []), c];
        return acc;
    }, {});

    const commentReplies = raw.replies.reduce((acc, r) => {
        acc[r.comment_id] = [...(acc[r.comment_id] || []), r];
        return acc;
    }, {});

    return {
        errors,
        data: {
            clientComments: raw.clientComments,
            tasks: raw.tasks,
            attendance: raw.attendance,
            dailyActivities: raw.dailyActivities,
            phases: raw.phases,
            dependencies: raw.dependencies,
            milestones: raw.milestones,
            baselineRevisions: raw.baselineRevisions,
            leaveRequests: raw.leaves,
            taskChallenges,
            teamData: teamMembers,
            budgetData: budgetWithCalculations,
            materialsData: raw.materials,
            performanceData: performanceWithNumbers,
            photosData: raw.photos,
            commentsData: raw.comments,
            commentReplies,
            invoicesData: invoicesWithNumbers,
            clientAccountData: clientAccountWithCorrectBalance,
            documentsData: raw.documents,
            budgetCategories: categoriesWithNumbers,
            budgetExpenses: expensesWithNumbers,
            budgetCommitments: commitmentsWithNumbers,
            rfis: raw.rfis,
            contacts: raw.contacts,
            hseIncidents: raw.hseIncidents,
        },
    };
}
